<template>
    <v-card
      class="grid-item__card"
      :class="{ 'grid-item__card--relabel': show_relabel_dropdown }"
      :data-bbox-id="image.bbox_id"
      elevation="1"
      :style="selected?'border: 3px solid #f7f5f3; background: #f7f5f3':''"
    >
      <v-card-subtitle>
        {{image.label}}<span v-if="image.certainty != null && image.certainty !== ''"> : {{image.certainty }}</span>
        <checkbox v-if="!no_toggle" style="float:right" :outlined-when-unchecked="true" :model-value="checked" @update:model-value="setChecked"></checkbox>
      </v-card-subtitle>
      <v-card-subtitle v-if="groupedAnnotationCount != null" class="pt-0">
        {{ groupedAnnotationCountLabel }}
      </v-card-subtitle>
      <v-card-subtitle v-if="validation">
        <span :style="{backgroundColor: background_color}">{{validation}}</span>
      </v-card-subtitle>
      <div v-if="hasStatusChips" class="grid-item__chips px-2 pt-1">
        <v-chip
          v-if="in_validation_set"
          x-small
          label
          outlined
          color="primary"
          class="mr-1 mb-1"
        >{{ $t('asset_grid.in_validation_set') }}</v-chip>
        <template v-if="status_chips_mode === 'myboxes'">
          <v-chip
            v-if="show_draft_annotation_chip"
            x-small
            label
            outlined
            color="grey"
            class="mr-1 mb-1"
          >{{ $t('asset_grid.asset_not_validated') }}</v-chip>
        </template>
        <template v-else>
          <v-chip
            x-small
            label
            outlined
            :color="asset_validated ? 'success' : 'grey'"
            class="mr-1 mb-1"
          >{{ asset_validated ? $t('asset_grid.asset_validated') : $t('asset_grid.asset_not_validated') }}</v-chip>
          <v-chip
            v-if="bbox_validated"
            x-small
            label
            outlined
            color="teal"
            class="mr-1 mb-1"
          >{{ $t('asset_grid.annotation_validated') }}</v-chip>
        </template>
      </div>
      <v-card-text>
        <div class="container grid-item__media" :class="{ 'grid-item__media--blur': sensitiveBlur }">
          <annotated-image
            v-if="showFullImage"
            class="grid-item__annotated"
            :max_height="148"
            @canvas_selected="selectImage(image.id || image.asset_id, image.bbox_id)"
            :image_url="image.image_url"
            :sensitive-blur="sensitiveBlur"
            :zoom_enabled="false"
            :hide_annotation_ui="true"
            :annotations="image.annotations"
          ></annotated-image>
          <template v-else>
            <img
              class="grid-item__img"
              @click="selectImage(image.id || image.asset_id, image.bbox_id)"
              :src="'data:image/png;base64, ' + image.data"
            />
            <div v-if="image.annotation_type === 2" class="centered white-font">+</div>
            <div v-if="image.annotation_type === 2" class="centered black-font">+</div>
          </template>
        </div>
      </v-card-text>
      <v-card-actions
        v-if="show_relabel_dropdown"
        class="grid-item__label-select px-2 py-2"
        @click.stop
      >
        <search-label-autocomplete
          class="grid-item__label-autocomplete"
          :model-value="image.label"
          :label_type="itemLabelType"
          :clear_on_select="false"
          :placeholder="$t('progress.search_field')"
          :ensure_text_line_label="itemLabelType === 'box'"
          :ensure_blackout_label="itemLabelType === 'box'"
          :allow_create_label="canManageLabels"
          :create_label_type="image.annotation_type === 2 ? 2 : 1"
          :disabled="relabeling"
          @label-selected="onItemLabelSelected"
        />
      </v-card-actions>
      <v-card-actions
        v-if="show_incorrect_button"
        class="grid-item__incorrect-action px-2 pb-2"
        @click.stop
      >
        <v-btn
          class="grid-item__incorrect-btn"
          data-testid="crop-incorrect-btn"
          size="small"
          color="error"
          variant="outlined"
          block
          :disabled="incorrectBusy"
          @click.stop="markIncorrect"
        >
          {{$t('myboxes.btn_incorrect') || 'Incorrect'}}
        </v-btn>
      </v-card-actions>
      <v-card-actions
        v-if="show_model_correct_button"
        class="grid-item__model-correct-action px-2 pb-2"
        @click.stop
      >
        <v-btn
          class="grid-item__model-correct-btn"
          data-testid="cm-model-correct-btn"
          size="small"
          color="success"
          variant="outlined"
          block
          :disabled="modelCorrectBusy"
          @click="markModelCorrect"
        >
          {{$t('confusion_matrix.model_is_correct') || 'Model is correct'}}
        </v-btn>
      </v-card-actions>
    </v-card>
</template>

<script>
import Checkbox from '@/components/Checkbox'
import SearchLabelAutocomplete from '@/components/SearchLabelAutocomplete.vue'
import AnnotatedImage from '@/components/AnnotatedImage'
import CurrentUserMixin from '@/mixins/CurrentUserMixin.vue'

export default {
  components: {Checkbox, SearchLabelAutocomplete, AnnotatedImage},
  mixins: [CurrentUserMixin],
  props: {
    image: {required: true},
    validation: {required: true},
    selected: {default: false},
    checked: {default: false},
    no_toggle: {default: false},
    /** 'myboxes': only validation-set + Draft (unvalidated annotation). Else full asset/annotation chips. */
    status_chips_mode: {
      type: String,
      default: 'default',
      validator: (v) => ['default', 'myboxes'].includes(v),
    },
    sensitiveBlur: {
      type: Boolean,
      default: false,
    },
    show_relabel_dropdown: {
      type: Boolean,
      default: false,
    },
    relabeling: {
      type: Boolean,
      default: false,
    },
    show_incorrect_button: {
      type: Boolean,
      default: false,
    },
    incorrectBusy: {
      type: Boolean,
      default: false,
    },
    show_model_correct_button: {
      type: Boolean,
      default: false,
    },
    modelCorrectBusy: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    setChecked(checked) {
      this.$emit('select_toggled', this.image.bbox_id, checked)
    },
    selectImage(assetId, bboxId) {
      this.$emit('select_item', assetId, bboxId)
    },
    remove(assetId, bboxId) {
      this.$emit('remove_item', assetId, bboxId)
    },
    markIncorrect() {
      this.$emit('mark_incorrect', this.image.bbox_id);
    },
    markModelCorrect() {
      this.$emit('model_correct', this.image.bbox_id);
    },
    onItemLabelSelected(label) {
      if (!label || !label.name || label.name === this.image.label) {
        return;
      }
      this.$emit('relabel', this.image.bbox_id, label.name);
    },
  },
  computed: {
    showFullImage() {
      return Boolean(
        this.image
        && this.image.image_url
        && Array.isArray(this.image.annotations),
      );
    },
    canManageLabels() {
      return this.can_manage_labels;
    },
    itemLabelType() {
      return this.image.annotation_type === 2 ? 'point' : 'box';
    },
    category () {
      if (this.image.translation) {
        return this.image.translation;
      }
      else {
        return this.image.label;
      }
    },
    background_color () {
        let red = "rgba(249,65,68)";
        let green = "rgba(144,190,109)"
        let orange = "rgba(248,150,30)"

        let validation = this.validation
        if(!validation) {
          return ''
        }
        
        if(validation.startsWith('Correct')) {
          return green;
        }
        else if(validation.startsWith('False negative')) {
          return orange;
        }
        else {
          return red;
        }
    },
    in_validation_set() {
      return this.image.in_validation_set === true;
    },
    asset_validated() {
      return this.image.asset_validated === true;
    },
    bbox_validated() {
      return this.image.bbox_validated === true;
    },
    show_draft_annotation_chip() {
      return !this.bbox_validated;
    },
    hasStatusChips() {
      if (this.status_chips_mode === 'myboxes') {
        return this.in_validation_set || this.show_draft_annotation_chip;
      }
      return true;
    },
    groupedAnnotationCount() {
      const count = this.image && this.image.count;
      if (count == null || count === '') {
        return null;
      }
      const n = Number(count);
      return Number.isFinite(n) ? n : null;
    },
    groupedAnnotationCountLabel() {
      const n = this.groupedAnnotationCount;
      if (n == null) {
        return '';
      }
      if (n === 1) {
        return `1 ${this.$t('set_label_groups.edit_annotation')}`;
      }
      return `${n} ${this.$t('set_label_groups.edit_annotations')}`;
    },
  },
}
</script>

<style scoped>
  .grid-item__card {
    min-height: 200px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .grid-item__card--relabel {
    overflow: visible;
    min-height: 240px;
  }
  .grid-item__card--relabel >>> .v-card__text {
    flex: 0 1 auto;
  }
  .grid-item__card >>> .v-card__text {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .grid-item__media {
    max-height: 148px;
    overflow: hidden;
    width: 100%;
  }
  .grid-item__media--blur .grid-item__img {
    filter: blur(28px);
    transform: scale(1.02);
  }
  .grid-item__media--blur >>> canvas {
    filter: blur(28px);
    transform: scale(1.02);
  }
  .grid-item__annotated {
    cursor: pointer;
  }
  .grid-item__annotated >>> canvas {
    cursor: pointer;
  }
  .grid-item__img {
    display: block;
    margin: 0 auto;
    max-height: 148px;
    max-width: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    cursor: pointer;
    vertical-align: top;
  }
  .container {
    position: relative;
    text-align: center;
  }
  .centered {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  .white-font {
    font-size: 35px;
    color: rgba(255, 255, 255, 0.8);
  }
  .black-font {
    font-size: 30px;
    color: rgba(0, 0, 0, 0.6);
  }
  .grid-item__label-select {
    width: 100%;
    flex-shrink: 0;
    display: block;
  }
  .grid-item__label-autocomplete {
    width: 100%;
  }
  .grid-item__label-select >>> .v-autocomplete,
  .grid-item__label-autocomplete >>> .v-autocomplete {
    max-width: 100%;
    width: 100%;
  }
  .grid-item__incorrect-action {
    width: 100%;
    flex-shrink: 0;
  }
  .grid-item__incorrect-btn {
    width: 100%;
  }
</style>